/**
 * Canonical prize-tier segment model.
 *
 * Single source of truth shared by:
 * - the admin prize-tier forms (segment dropdown labels), and
 * - settlement / session resolution (the allowlist used to award prizes).
 *
 * Keep `ref` values in sync with Caesars' segmentation model. A missing or
 * incorrect ref causes that tier's players to fall back to the default prize.
 */
/** Baseline ladder used when no segment is assigned or the segment is unknown. */
export const DEFAULT_PRIZE_TIER_SEGMENT = 'default';
/** The baseline `default` ladder plus the nine Caesars tier segments. */
export const PRIZE_TIER_SEGMENTS = [
    { ref: DEFAULT_PRIZE_TIER_SEGMENT, label: 'Default' },
    { ref: '249357b7-305e-4d05-9585-2a5d4affddb0', label: 'Tier 1' },
    { ref: '55749dd0-b4c1-4369-8c28-daf94eead07e', label: 'Tier 2' },
    { ref: '95829538-f7b2-4069-ba96-1ddf86687379', label: 'Tier 3' },
    { ref: 'bca8cc21-c206-4815-ab1b-9c97758c6062', label: 'Tier 4' },
    { ref: '826e06cc-4ca3-4bc6-b6f5-38234c0abdb8', label: 'Tier 5' },
    { ref: 'a3f3a5f4-58b9-4609-ab05-6d9ba90b0f0d', label: 'Tier 6' },
    { ref: 'e2046534-836f-417f-98b9-968a5dc05c18', label: 'Tier 7' },
    { ref: '0cc93008-25ac-4bfb-96ea-5afe211e0bba', label: 'Tier 8' },
    { ref: 'f67353ce-bad8-4d39-8f16-4b32b78d67ef', label: 'Tier 9' },
];
/** Refs for `default` + tiers 1–9, derived from {@link PRIZE_TIER_SEGMENTS}. */
export const PRIZE_TIER_SEGMENT_REFS = PRIZE_TIER_SEGMENTS.map((segment) => segment.ref);
const KNOWN_PRIZE_TIER_SEGMENT_REFS = new Set(PRIZE_TIER_SEGMENT_REFS);
export const isKnownPrizeTierSegment = (ref) => KNOWN_PRIZE_TIER_SEGMENT_REFS.has(ref);
/** Trim a raw segment value, coalescing null/blank/whitespace to `default`. */
export const normaliseUserSegment = (segment) => {
    const trimmed = segment?.trim();
    return trimmed && trimmed.length > 0 ? trimmed : DEFAULT_PRIZE_TIER_SEGMENT;
};
/**
 * Resolve the segment ref used for prize-tier lookup.
 *
 * Blank values become `default`. Segments outside `default` + tiers 1–9 are
 * treated as a misconfiguration and also map to `default`, so those players
 * still receive the baseline prize (CAE-188).
 */
export const resolvePrizeSegmentRef = (segment) => {
    const normalised = normaliseUserSegment(segment);
    return isKnownPrizeTierSegment(normalised) ? normalised : DEFAULT_PRIZE_TIER_SEGMENT;
};
//# sourceMappingURL=prize-tier-segments.js.map